export interface LpiOrder {
  transport_order_number: string;
  client_id?: string;
  clients?: string[];
  execution_date: string;
  num_shipments: number;
  total_beladingsprestatie_tonkm: number;
  total_add_km: number;
  total_gcd_km?: number;
  // null when LPI could not be computed for this order (see `errors`)
  lpi_value: number | null;
  distance_source: string;
  distance_quality_score?: number;
  has_complete_add: boolean;
  benuttingsgraad?: number | null;
  // Reason codes explaining why LPI/benuttingsgraad could not be computed
  errors?: string[];
}

export interface LpiSummary {
  total_orders: number;
  total_beladingsprestatie: number;
  total_add_km: number;
  average_lpi: number | null;
  overall_lpi: number | null;
  /**
   * Weighted capacity utilisation as a percentage — ton-km moved over ton-km
   * that could have been moved. Null when no order in the filtered set carries
   * a max capacity (it arrives with the ADD ingest and is often absent).
   */
  average_efficiency: number | null;
  /** 100 − average_efficiency. Null whenever average_efficiency is null. */
  improvement_potential: number | null;
}

export interface LpiPagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
}

export interface LpiResponse {
  orders: LpiOrder[];
  summary: LpiSummary;
  pagination?: LpiPagination;
}

export interface PickupDeliveryCoordinates {
  latitude: number;
  longitude: number;
}

export interface TransportOrderLpi {
  transport_order_number: string;
  execution_date: string;
  num_shipments: number;
  total_beladingsprestatie_tonkm: number;
  total_add_km: number;
  lpi_value: number;
  distance_source: string;
  distance_quality_score: number;
  calculation_timestamp: string;
}

export interface ShipmentLpi {
  order_number: string;
  volgnummer: string;
  transport_order_number: string;
  execution_date: string;
  client_id?: string;
  vehicle_id: string;
  weight_tons: number;
  gcd_km: number;
  add_km: number;
  beladingsprestatie_tonkm: number;
  lpi_value: number;
  benuttingsgraad?: number | null;
  data_quality: string;
  pickup_coordinates: PickupDeliveryCoordinates;
  delivery_coordinates: PickupDeliveryCoordinates;
}

export interface ClientLpi {
  client_id: string;
  num_orders: number;
  num_shipments: number;
  total_tonkm: number;
  total_distance_km: number;
  aggregate_lpi: number;
}

export interface ClientLpiSummary {
  total_clients: number;
  total_tonkm: number;
  total_distance_km: number;
  overall_lpi: number;
}

export interface ClientLpiResponse {
  clients: ClientLpi[];
  summary: ClientLpiSummary;
}

// Filter types
/**
 * The filters that live behind the Advanced Filters drawer rather than inline
 * on the bar. A subset of LpiFilters so the drawer and the pills can be typed
 * without either of them knowing about dates, client or transport order.
 */
export interface LpiAdvancedSelection {
  vehicle_id: string[];
  modality_detail: string[];
  carrier_id: string[];
}

export const EMPTY_LPI_ADVANCED: LpiAdvancedSelection = {
  vehicle_id: [],
  modality_detail: [],
  carrier_id: [],
};

export interface LpiFilters {
  client_id: string[];
  start_date: string;
  end_date: string;
  vehicle_id: string[];
  vehicle_type: string[];
  modality_detail: string[];
  carrier_id: string[];
  // Exact transport order numbers. Unlike the dropdown-backed filters there is
  // no options list to pick from — orders are unbounded — so these are typed
  // in, and matched exactly.
  transport_order_number: string[];
  // When true, only orders that have an LPI value are requested/shown.
  lpi_only: boolean;
}

export interface ClientListResponse {
  clients: string[];
  count: number;
}

// The /lpi/vehicles/list dropdown endpoint returns a flat array of vehicle-ID
// strings. It has, in the past, been accidentally pointed at the richer
// Vehicles-viewer payload (objects), which crashes the <select> with React
// error #31, so the type stays tolerant and the caller normalizes to strings.
export interface VehicleListResponse {
  vehicles: Array<string | { vehicleId: string }>;
  count: number;
}

export interface VehicleTypeListResponse {
  vehicle_types: string[];
  count: number;
}

export interface ModalityDetailListResponse {
  modality_details: string[];
  count: number;
}

export interface CarrierListResponse {
  carriers: string[];
  count: number;
}

// File upload types
export enum FileType {
  TMS = 'tms',
  TRANSPORT_ORDER = 'transport_order',
  ADD = 'add',
  UNKNOWN = 'unknown',
}

export interface UploadFileMetadata {
  file: File;
  type: FileType;
  detectedType: FileType;
}

// Top-level tabs on the LPI Reports page. Mirrors CpiReportsTab
// ('dashboard' | 'trip-details') in src/pages/cpiReports/types.ts.
export type LpiReportsTab = 'dashboard' | 'details';

// --- Dashboard aggregates (Phase 2 endpoints) ---

export interface LpiCoverage {
  with_lpi: number;
  without_lpi: number;
  total: number;
  with_lpi_pct: number | null;
}

/**
 * One bar of a Dashboard chart. `bucket` is a month ("2025-11") for the monthly
 * chart and a vehicle id for the by-vehicle chart.
 *
 * `lpi` is null when the bucket has no usable ADD — render it as absent, never
 * as zero. `orders` is NOT additive across by-vehicle buckets: an order that
 * spans two vehicles counts toward both.
 */
export interface LpiAggregateBucket {
  bucket: string;
  orders: number;
  lpi: number | null;
  total_belad: number;
  total_add: number;
  /**
   * Weighted capacity utilisation for the bucket, as a percentage — computed
   * server-side the same way as the summary's `average_efficiency`, so the
   * chart reconciles with the KPI card.
   *
   * Optional because it is absent on responses from a backend that predates
   * it, and null whenever no order in the bucket carries a capacity. Both mean
   * "not measured" and must render as a gap, never as 0.
   */
  benuttingsgraad?: number | null;
}

export interface LpiBucketsResponse {
  buckets: LpiAggregateBucket[];
}
